import { useMutation } from '@platform/api-client';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { useTenantDetail } from '../catalog/useCatalogQueries.ts';
import { useState } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { CarrierAccountSheet } from './CarrierAccountSheet.tsx';
import { CarrierTiles } from './CarrierTiles.tsx';
import { useCarriers, type Carrier, type CarrierAccountBody } from './carriers.ts';

export type CarrierConnectionsProps = { api: TypedClient; shipping: ShippingClient; canManage: boolean };

export const CarrierConnections = ({ api, shipping, canManage }: CarrierConnectionsProps) => {
  const { t, notify, invalidate, describeError } = usePageContext();
  const carriers = useCarriers(shipping);
  const currency = useTenantDetail(api).data?.currency;
  const [editing, setEditing] = useState<Carrier | null>(null);
  const save = useMutation({
    mutationFn: ({ carrier, body }: { carrier: Carrier; body: CarrierAccountBody }) =>
      shipping.put('/admin/carriers/{code}/account', { path: { code: carrier.code }, body }),
    onSuccess: async (_account, { carrier }) => {
      notify(t('carriers.saved', { carrier: carrier.name }));
      setEditing(null);
      await invalidate('carriers');
    },
  });

  return (
    <>
      <CarrierTiles
        query={carriers}
        canManage={canManage}
        onEdit={(carrier) => {
          save.reset();
          setEditing(carrier);
        }}
      />
      {editing === null || currency === undefined ? null : (
        <CarrierAccountSheet
          carrier={editing}
          currency={currency}
          submitting={save.isPending}
          errorMessage={save.isError ? (describeError(save.error) ?? null) : null}
          onClose={() => {
            setEditing(null);
          }}
          onSubmit={(body) => {
            save.mutate({ carrier: editing, body });
          }}
        />
      )}
    </>
  );
};
